import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Plus, Power } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { api, errMsg, PRODUCT_LABEL } from "../../lib/api";
import { BadgeIcon, BADGE_ICON } from "../../components/Icons";
import { PageHead, Stat } from "./parts";

const UserRow = ({ u, badges, onPatch }) => (
  <tr data-testid={`admin-user-row-${u.username}`} className="border-b border-[#1a1a22] text-sm hover:bg-white/[0.02]">
    <td className="px-4 py-3"><a href={`/${u.username}`} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-[#ff3b91]">@{u.username}</a><div className="font-mono text-[11px] text-zinc-500">{u.email}</div></td>
    {["email_verified", "premium", "verified", "banned"].map((k) => (
      <td key={k} className="px-3 py-3"><Switch data-testid={`admin-toggle-${k}-${u.username}`} checked={!!u[k]} onCheckedChange={(v) => onPatch(u.id, { [k]: v })} className={k === "banned" ? "data-[state=checked]:bg-red-500" : "data-[state=checked]:bg-[#ff3b91]"} /></td>
    ))}
    <td className="px-3 py-3">
      <select data-testid={`admin-role-select-${u.username}`} value={u.role} onChange={(e) => onPatch(u.id, { role: e.target.value })} className="rounded-lg border border-[#262630] bg-[#0b0b0e] px-2 py-1 text-xs">
        <option value="user">user</option><option value="admin">admin</option>
      </select>
    </td>
    <td className="px-3 py-3">
      <Popover>
        <PopoverTrigger asChild><button data-testid={`admin-badges-button-${u.username}`} className="flex items-center gap-1 rounded-lg border border-[#262630] px-2 py-1 text-xs text-zinc-300 hover:border-[#ff3b91]/50">{(u.badges || []).length} badges</button></PopoverTrigger>
        <PopoverContent className="w-56 border-[#262630] bg-[#0d0d10] p-2">
          {badges.length === 0 && <div className="p-2 text-xs text-zinc-500">create badges first</div>}
          {badges.map((b) => {
            const on = (u.badges || []).includes(b.id);
            return (
              <button key={b.id} data-testid={`admin-assign-badge-${b.id}-${u.username}`} onClick={() => onPatch(u.id, { badges: on ? u.badges.filter((x) => x !== b.id) : [...(u.badges || []), b.id] })} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${on ? "bg-[#ff3b91]/15 text-white" : "text-zinc-400 hover:bg-white/5"}`}>
                <BadgeIcon icon={b.icon} style={{ color: b.color }} />{b.name}
              </button>
            );
          })}
        </PopoverContent>
      </Popover>
    </td>
    <td className="px-3 py-3 font-mono text-xs text-zinc-400">{u.views}</td>
  </tr>
);

const BadgesTab = ({ badges, reload }) => {
  const [f, setF] = useState({ name: "", icon: "star", color: "#ff3b91", description: "" });
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/badges", f); toast.success("Badge created"); setF({ ...f, name: "", description: "" }); reload(); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const del = async (id) => { await api.delete(`/admin/badges/${id}`); reload(); toast.success("Badge deleted"); };
  return (
    <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
      <form onSubmit={create} className="panel space-y-3 p-5" data-testid="admin-badge-form">
        <input className="field" placeholder="badge name" maxLength={24} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-badge-name-input" />
        <input className="field" placeholder="description" maxLength={80} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="admin-badge-desc-input" />
        <div className="flex flex-wrap gap-1.5">{Object.keys(BADGE_ICON).map((ic) => (
          <button type="button" key={ic} onClick={() => setF({ ...f, icon: ic })} data-testid={`admin-badge-icon-${ic}`} className={`rounded-md border p-1.5 ${f.icon === ic ? "border-[#ff3b91]" : "border-[#262630]"}`}><BadgeIcon icon={ic} style={{ color: f.color }} /></button>
        ))}</div>
        <input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-10 w-14 rounded border border-[#262630] bg-transparent" data-testid="admin-badge-color-input" />
        <button className="btn-pink w-full" data-testid="admin-badge-create-button"><Plus className="h-4 w-4" /> create badge</button>
      </form>
      <div className="grid content-start gap-3 sm:grid-cols-2">
        {badges.map((b) => (
          <div key={b.id} data-testid={`admin-badge-${b.id}`} className="panel flex items-center gap-3 p-4">
            <BadgeIcon icon={b.icon} className="h-5 w-5" style={{ color: b.color, filter: `drop-shadow(0 0 6px ${b.color})` }} />
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{b.name}</div><div className="truncate text-xs text-zinc-500">{b.description}</div></div>
            <button onClick={() => del(b.id)} className="text-zinc-500 hover:text-red-400" data-testid={`admin-badge-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const DomainsTab = () => {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/admin/domains").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); try { await api.post("/admin/domains", { name }); setName(""); load(); toast.success("Domain added"); } catch (e2) { toast.error(errMsg(e2)); } };
  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-zinc-500">Alias domains users can pick for their profile and image links. Point each domain's DNS at this app after deploying.</p>
      <form onSubmit={add} className="flex gap-2"><input className="field" placeholder="example.gg" value={name} onChange={(e) => setName(e.target.value)} required data-testid="admin-domain-input" /><button className="btn-pink" data-testid="admin-domain-add-button">add</button></form>
      {list.map((d) => (
        <div key={d.id} data-testid={`admin-domain-${d.name}`} className="panel flex items-center justify-between px-5 py-3">
          <span className={`font-mono text-sm ${d.active ? "text-white" : "text-zinc-600 line-through"}`}>{d.name}</span>
          <div className="flex gap-3">
            <button onClick={async () => { await api.patch(`/admin/domains/${d.id}`); load(); }} className="text-zinc-400 hover:text-[#ff3b91]" data-testid={`admin-domain-toggle-${d.name}`}><Power className="h-4 w-4" /></button>
            <button onClick={async () => { await api.delete(`/admin/domains/${d.id}`); load(); }} className="text-zinc-400 hover:text-red-400" data-testid={`admin-domain-delete-${d.name}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PurchasesTab = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/purchases").then(({ data }) => setList(data)); }, []);
  return (
    <div className="panel overflow-x-auto" data-testid="admin-purchases-table">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#1a1a22] text-left"><th className="eyebrow px-4 py-3">user</th><th className="eyebrow px-4 py-3">product</th><th className="eyebrow px-4 py-3">amount</th><th className="eyebrow px-4 py-3">status</th><th className="eyebrow px-4 py-3">date</th></tr></thead>
        <tbody>{list.map((p) => (
          <tr key={p.session_id} className="border-b border-[#1a1a22]"><td className="px-4 py-3">@{p.username}</td><td className="px-4 py-3">{PRODUCT_LABEL[p.product]}</td><td className="px-4 py-3 font-mono">€{p.amount?.toFixed(2)}</td>
            <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${p.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"}`}>{p.payment_status}</span></td>
            <td className="px-4 py-3 font-mono text-xs text-zinc-500">{new Date(p.created_at).toLocaleString()}</td></tr>
        ))}</tbody>
      </table>
      {list.length === 0 && <div className="p-6 text-sm text-zinc-500">no purchases yet.</div>}
    </div>
  );
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [badges, setBadges] = useState([]);
  const [q, setQ] = useState("");
  const loadUsers = (query = q) => api.get("/admin/users", { params: { q: query } }).then(({ data }) => setUsers(data));
  const loadBadges = () => api.get("/admin/badges").then(({ data }) => setBadges(data));
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); loadUsers(""); loadBadges(); }, []);
  const patch = async (id, body) => {
    try { const { data } = await api.patch(`/admin/users/${id}`, body); setUsers((x) => x.map((u) => (u.id === id ? data : u))); toast.success("User updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead eyebrow="admin" title="control room" />
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="users" value={stats?.users ?? "–"} testid="admin-stat-users" accent />
        <Stat label="premium" value={stats?.premium ?? "–"} testid="admin-stat-premium" />
        <Stat label="revenue" value={stats ? `€${stats.revenue.toFixed(2)}` : "–"} testid="admin-stat-revenue" />
        <Stat label="total views" value={stats?.views?.toLocaleString() ?? "–"} testid="admin-stat-views" />
      </div>
      <Tabs defaultValue="users">
        <TabsList className="mb-6 h-auto flex-wrap justify-start gap-1 rounded-xl border border-[#1f1f28] bg-[#0b0b0e] p-1">
          {["users", "badges", "domains", "purchases"].map((t) => <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="rounded-lg px-4 py-2 text-xs uppercase tracking-wider text-zinc-400 data-[state=active]:bg-[#ff3b91] data-[state=active]:text-white">{t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="users">
          <form onSubmit={(e) => { e.preventDefault(); loadUsers(); }} className="mb-4 flex max-w-md items-center gap-2 rounded-xl border border-[#262630] bg-[#0b0b0e] px-3">
            <Search className="h-4 w-4 text-zinc-500" /><input className="flex-1 bg-transparent py-2.5 text-sm outline-none" placeholder="search username or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search-input" />
          </form>
          <div className="panel overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full min-w-[820px]">
              <thead><tr className="border-b border-[#1a1a22] text-left">{["user", "email ok", "premium", "verified", "banned", "role", "badges", "views"].map((h) => <th key={h} className="eyebrow px-3 py-3 first:px-4">{h}</th>)}</tr></thead>
              <tbody>{users.map((u) => <UserRow key={u.id} u={u} badges={badges} onPatch={patch} />)}</tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="badges"><BadgesTab badges={badges} reload={loadBadges} /></TabsContent>
        <TabsContent value="domains"><DomainsTab /></TabsContent>
        <TabsContent value="purchases"><PurchasesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
